import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Input, Label, FormFeedback } from "reactstrap";

const BasicInfoForm = ({ validation }: { validation: any }) => {
  const [base64Img, setBase64Img] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const imageUrl = validation.values.product_image_url;
    if (!imageUrl) return;

    const isAlreadyBase64 = typeof imageUrl === "string" && imageUrl.startsWith("data:");
    const setImage = (val: string) => {
      setBase64Img(val);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    if (isAlreadyBase64) {
      setImage(imageUrl);
      return;
    }

    // When editing an existing product, convert the stored URL to base64 so updates
    // (e.g., category changes) still send a base64 image payload even if the image
    // file is unchanged.
    const fetchAndConvert = async () => {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          setImage(base64);
          validation.setFieldValue("product_image_url", base64, false);
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error("Failed to convert image URL to base64", err);
        setImage(imageUrl); // fallback to original value
      }
    };

    fetchAndConvert();
  }, [validation.values.product_image_url, validation]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    validation.setFieldValue("product_image", file);

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result as string;
      setBase64Img(base64);
      validation.setFieldValue("product_image_url", base64); // Save to Formik
    };
  };

  const removeImage = () => {
    setBase64Img(null);
    validation.setFieldValue("product_image_url", ""); // clear
  };

  return (
    <Row className="g-3">

      {/* Product Category */}
      <Col md={6}>
        <Label className="fw-bold">Product Category</Label>
        <Input
          type="text"
          name="product_category"
          value={validation.values.product_category}
          onChange={validation.handleChange}
          invalid={!!(validation.touched.product_category && validation.errors.product_category)}
          placeholder="Insurance"
        />
        <FormFeedback>{validation.errors.product_category}</FormFeedback>
      </Col>

      {/* Product Name */}
      <Col md={6}>
        <Label className="fw-bold">Product Name</Label>
        <Input
          type="text"
          name="product_name"
          value={validation.values.product_name}
          onChange={validation.handleChange}
          invalid={!!(validation.touched.product_name && validation.errors.product_name)}
          placeholder="Type here.."
        />
        <FormFeedback>{validation.errors.product_name}</FormFeedback>
      </Col>

      {/* Product Code */}
      <Col md={6}>
        <Label className="fw-bold">Product Code</Label>
        <Input
          type="text"
          name="product_code"
          value={validation.values.product_code}
          onChange={validation.handleChange}
          invalid={!!(validation.touched.product_code && validation.errors.product_code)}
          placeholder="P00001"
        />
        <FormFeedback>{validation.errors.product_code}</FormFeedback>
      </Col>

      {/* Plan Type */}
      <Col md={6}>
        <Label className="fw-bold">Plan Type</Label>
        <Input
          type="text"
          name="plan_type"
          value={validation.values.plan_type}
          onChange={validation.handleChange}
          invalid={!!(validation.touched.plan_type && validation.errors.plan_type)}
          placeholder="Term Plan"
        />
        <FormFeedback>{validation.errors.plan_type}</FormFeedback>
      </Col>

      {/* Cover Image */}
      <Col md={6}>
        <Label className="fw-bold">Cover Image</Label>
        <div className="d-flex align-items-center">
          <Input
            type="file"
            name="product_image_url"
            accept="image/png, image/jpeg"
            innerRef={fileInputRef}
            onChange={handleImageChange}
            className="me-2"
            invalid={!!(validation.touched.product_image_url && validation.errors.product_image_url)}
          />
          <FormFeedback>{validation.errors.product_image_url}</FormFeedback>
        </div>
        <small className="text-muted">PNG/JPG/ JPEG format (Max 10MB)</small>

        {base64Img && (
          <div className="position-relative mt-2">
            <img
              src={base64Img}
              alt="Preview"
              style={{ width: 120, height: 100, borderRadius: "6px" }}
            />
            <button
              type="button"
              className="btn btn-sm btn-danger position-absolute top-0 end-0 rounded-circle"
              style={{ padding: "2px 6px" }}
              onClick={removeImage}
            >
              ✕
            </button>

          </div>
        )}
      </Col>

      {/* Description */}
      <Col md={12}>
        <Label className="fw-bold">Product Description</Label>
        <Input
          type="textarea"
          name="product_description"
          rows={4}
          placeholder="Type here.."
          value={validation.values.product_description}
          onChange={validation.handleChange}
          invalid={!!(validation.touched.product_description && validation.errors.product_description)}
        />
        <FormFeedback>{validation.errors.product_description}</FormFeedback>
      </Col>
    </Row>
  );
};

export default BasicInfoForm;
